import RankedAPI from "@api/lol/Ranked.api";
import type { AppRegion } from "@core-types/region.enum";
import type IPagination from "@fetcher/models/IPagination";
import { useAppRegion } from "@hooks/region/useAppRegion";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import type { IRankedSmurfCardItem } from "../models/IRankedSmurf.models";

export interface ISkinOption {
  name: string;
  id: number;
  thumbnail: string;
}

export const useSmurfsRankedFilters = () => {
  const [ranksFilters, setRanksFilters] = useState<string[]>([]);
  const [priceRangesFilters, setPriceRangesFilters] = useState<string[]>([]);
  const [skinsFilters, setSkinsFilters] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [skinsList, setSkinsList] = useState<ISkinOption[]>([]);
  const { changeRegion } = useAppRegion();

  const [rankedAccounts, setRankedAccounts] =
    useState<IPagination<IRankedSmurfCardItem>>();

  const [searchParams] = useSearchParams();

  const onPaginate = (page: number) => {
    setCurrentPage(page);
  };

  // need to specify region in deps to reload data on region change
  const loadData = useCallback(() => {
    RankedAPI.list({
      page: currentPage,
      ranks: ranksFilters,
      price: priceRangesFilters,
      skins: skinsFilters,
    }).then((res) => {
      setRankedAccounts(res.data);
      if (currentPage > res.data.meta.lastPage) setCurrentPage(1);
    });
  }, [currentPage, ranksFilters, priceRangesFilters, skinsFilters]);

  const handleRegionChange = (newRegion: AppRegion) => {
    changeRegion(newRegion, loadData);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    RankedAPI.skinList().then((res) => {
      setSkinsList(
        res.data.map((skin) => ({
          name: skin.name,
          id: skin.id,
          thumbnail: skin.thumbnail,
        })),
      );
    });
  }, []);

  useEffect(() => {
    const url = new URL(
      `${location.protocol}//${location.host}${location.pathname}`,
    );

    if (ranksFilters.length > 0) {
      for (const rank of ranksFilters) {
        url.searchParams.append("ranks", rank);
      }
    }

    if (priceRangesFilters.length > 0) {
      for (const price of priceRangesFilters) {
        url.searchParams.append("price", price);
      }
    }

    if (skinsFilters.length > 0) {
      for (const skin of skinsFilters) {
        url.searchParams.append("skins", skin);
      }
    }

    if (currentPage > 1) {
      url.searchParams.append("page", currentPage.toString());
    }

    window.history.replaceState({}, "", url.toString());
  }, [ranksFilters, priceRangesFilters, skinsFilters, currentPage]);

  useEffect(() => {
    const ranks = searchParams.getAll("ranks");
    const price = searchParams.getAll("price");
    const skins = searchParams.getAll("skins");
    const page = searchParams.get("page");

    setRanksFilters(ranks);
    setPriceRangesFilters(price);
    setSkinsFilters(skins);

    setCurrentPage(page ? Number.parseInt(page) : 1);
  }, [searchParams]);

  return {
    rankedAccounts,
    filters: {
      ranks: ranksFilters,
      price: priceRangesFilters,
      skins: skinsFilters,
      page: currentPage,
    },
    setters: {
      setRanksFilters,
      setPriceRangesFilters,
      setSkinsFilters,
    },
    handlers: {
      onPaginate,
      handleRegionChange,
    },
    skinsList,
  };
};
